import * as React from 'react';
import classNames from 'classnames';
import { ButtonRegular } from '../Button';
import { Icon } from '../Icon';
import styles from './Accordion.module.scss';

export interface AccordionProps {
	readonly title: string | React.ReactNode;
	readonly id: string;
	readonly isExpanded?: boolean;
	readonly onToggle?: (isExpanded: boolean) => void;
	readonly className?: string;
	readonly contentClassName?: string;
	readonly contentInnerClassName?: string;
	readonly children: React.ReactNode;
}

/**
 * Generic Accordion component, used for displaying content in an accordion / collapse.
 */
export const Accordion: React.FunctionComponent<AccordionProps> = ({
	title,
	id,
	isExpanded,
	onToggle,
	className,
	contentClassName,
	contentInnerClassName,
	children,
}) => {
	const labelId = 'label' + id;

	const [expanded, setExpanded] = React.useState(isExpanded || false);

	const handleClick = (): void => {
		setExpanded(!expanded);

		if (!onToggle) return;
		onToggle(!expanded);
	};

	return (
		<>
			<h2 className={styles.titleWrapper}>
				<ButtonRegular
					className={classNames(styles.titleButton, className)}
					hasNoStyles
					id={labelId}
					aria-expanded={expanded}
					aria-controls={id}
					onClick={handleClick}
				>
					{typeof title === 'string' ? <span className={styles.title}>{title}</span> : <>{title}</>}
					<Icon
						className={styles.icon}
						name={'chevronDown'}
						size={'md'}
						rotate={expanded ? '180' : undefined}
					/>
				</ButtonRegular>
			</h2>
			<div className={styles.panel} id={id} role={'region'} aria-labelledby={labelId} aria-hidden={!expanded}>
				<div className={classNames(styles.contentWrapper, contentClassName)}>
					<div className={classNames(styles.contentInner, contentInnerClassName)}>{children}</div>
				</div>
			</div>
		</>
	);
};
